import * as ko from 'knockout';

import { ListRequestParams } from '../api/request';
import { ListLoader, ListLoaderDelegate, delegateActions } from '../components/list_loader';
import * as svApi from '../api/scheduled_visits';
import { ListHeaderAction } from '../components/list_header';
import i18n from '../i18n';
import { translate } from '../i18n_text';
import {
  canCreateScheduledVisit,
  canDeleteScheduledVisit,
  canEditScheduledVisit,
  reactListTablesEnabled,
} from '../permissions';
import { updateLocationWithQueryString } from '../utils';
import { session } from '../session';
import { GridView } from '../utils/grid_view';
import {
  ScheduledVisitsListPage,
  ScheduledVisitsListProps,
  ScheduledVisitsListQuery,
  ScheduledVisitsListRow,
} from 'react-islands';

let template = require('raw-loader!../../templates/scheduled_visit_library.html').default;

class ScheduledVisitLibraryScreen implements ListLoaderDelegate<svApi.ScheduledVisitData> {
  canCreate = canCreateScheduledVisit();
  canEdit = canEditScheduledVisit();
  canDelete = canDeleteScheduledVisit();
  listActions = ko.observableArray<ListHeaderAction>([]);

  newScheduledVisitsTable = reactListTablesEnabled();

  private nameFilter = ko.observable('').extend({ throttle: 300 });
  searchPlaceholder = i18n.t('Search scheduled visit')();
  private listLoader: ListLoader<svApi.ScheduledVisitData>;
  private subscriptions: KnockoutSubscription[] = [];

  private view = new GridView('scheduled-visits');

  tableProps = ko.pureComputed<ScheduledVisitsListProps>(() => ({
    view: this.view,
    fetchPage: this.fetchPage,
  }));

  constructor(params: { filters: { name_prefix?: string } }) {
    this.nameFilter(params.filters.name_prefix || '');

    if (this.canCreate) {
      this.listActions.push({
        title: i18n.t('Add')(),
        icon: 'add_circle',
        href: '/scheduled_visit_library/new/',
        tooltipTitle: i18n.t('Add scheduled visit')(),
      });
    }

    if (this.newScheduledVisitsTable) {
      this.view.load(params.filters);
    }
  }

  onReady(loader: ListLoader<svApi.ScheduledVisitData>) {
    this.listLoader = loader;
    this.subscriptions.push(this.nameFilter.subscribe(() => this.listLoader.forceLoad()));
  }

  dispose() {
    this.subscriptions.forEach((s) => s.dispose());
    this.subscriptions = [];
  }

  fetch(params: ListRequestParams) {
    const filters = { name_prefix: this.nameFilter() };
    updateLocationWithQueryString(filters);
    return svApi.list({ ...params, ...filters });
  }

  instantiate(data: svApi.ScheduledVisitData) {
    return data;
  }

  remove(id: string) {
    return svApi.remove(id);
  }

  canRemove(data: svApi.ScheduledVisitData) {
    return this.canDelete;
  }

  private fetchPage = (query: ScheduledVisitsListQuery): Promise<ScheduledVisitsListPage> =>
    this.view.fetchListPage(query, svApi.list, 'name_prefix', this);

  toRow = (scheduledVisit: svApi.ScheduledVisitData): ScheduledVisitsListRow => ({
    id: scheduledVisit.id,
    name: translate(scheduledVisit.name_json),
    url: session.toTenantPath(this.editPath(scheduledVisit)),
    code: scheduledVisit.code,
    actions: delegateActions(this, scheduledVisit, this.view.reload),
  });

  private editPath(scheduledVisit: svApi.ScheduledVisitData): string {
    return '/scheduled_visit_library/' + scheduledVisit.id + '/';
  }
}

export const scheduledVisitLibrary = {
  name: 'scheduled-visit-library',
  viewModel: ScheduledVisitLibraryScreen,
  template,
};

ko.components.register(scheduledVisitLibrary.name, scheduledVisitLibrary);
